import { Button, Popover } from '@material-ui/core';
import cc from 'classcat';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import classes from './DateTimePicker.module.scss';
import { DEFAULT_FORMAT, getFnsLocale, stringifyDateTime } from './helpers/utils';
import { addMonths, format } from 'date-fns';
import TimeSelection from './TimeSelection/TimeSelection';
import { DayPicker, DayPickerBase } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import DayPickerCaption from './DayPickerCaption/DayPickerCaption';
import useSelector from '../../../../../infrastructure/useSelector';

export default function DateTimePicker({
    onDateTimeChange,
    disabled,
    disabledDays,
    value,
    prefixText,
    label,
    monthAndYearSelectionFromMonth = new Date(),
    monthAndYearSelectionToMonth,
    dataTestId
}: {
    onDateTimeChange: (day: Date) => void;
    value: Date;
    prefixText?: string;
    label?: ReactNode;
    disabled?: boolean;
    disabledDays: DayPickerBase['disabled'];
    monthAndYearSelectionFromMonth?: Date;
    monthAndYearSelectionToMonth?: Date;
    dataTestId?: string;
}): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const anchorEl = useRef(null);
    const toggle = useCallback(() => setIsOpen(!isOpen), [isOpen]);
    const languageCode = useSelector(state => state.ui.languageCode);
    const locale = getFnsLocale(languageCode);

    useEffect(
        () => () => {
            setIsOpen(false);
        },
        []
    );

    return (
        <div className={cc([classes.WMDateTimePicker])}>
            {label && <label>{label}</label>}
            <Button
                ref={anchorEl}
                disabled={disabled}
                classes={{
                    root: cc([classes.pickerBtn, { [classes.open]: isOpen }]),
                    disabled: classes.disabled
                }}
                onClick={toggle}
                variant="text"
                disableRipple
                disableFocusRipple
                data-testid={`${dataTestId}-button`}
            >
                <div className={classes.content}>
                    <span {...(dataTestId ? { 'data-testid': dataTestId } : {})} className={classes.dateText}>{`${
                        prefixText != undefined ? prefixText : ''
                    } ${stringifyDateTime(value, DEFAULT_FORMAT)}`}</span>
                </div>
            </Button>

            <Popover
                open={isOpen}
                anchorEl={anchorEl.current}
                onClose={() => {
                    setIsOpen(false);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                PaperProps={{ classes: { root: classes.dateTimePopoverPaper }, ...{ ['data-testid']: `${dataTestId}-datepicker` } }}
            >
                <>
                    <DayPicker
                        mode="single"
                        classNames={{
                            root: classes.dateTimePicker,
                            months: classes.months,
                            month: classes.month,
                            head_cell: classes.head_cell,
                            table: classes.table,
                            tbody: classes.tbody,
                            day: classes.day,
                            day_selected: classes.daySelected,
                            tfoot: classes.tfoot
                        }}
                        disabled={disabledDays}
                        selected={value}
                        defaultMonth={value}
                        onSelect={day => day && onDateTimeChange(day)}
                        formatters={{
                            formatWeekdayName: weekday => format(weekday, 'ccc', { locale }),
                        }}
                        components={{
                            Caption: props => (
                                <DayPickerCaption
                                    {...props}
                                    dataTesid={dataTestId}
                                    fromMonth={monthAndYearSelectionFromMonth}
                                    toMonth={monthAndYearSelectionToMonth || addMonths(value, 60)}
                                />
                            )
                        }}
                    />
                    <div className={classes.footer}>
                        <TimeSelection date={value} onChange={onDateTimeChange} />
                    </div>
                </>
            </Popover>
        </div>
    );
}
